import type { ComplianceCell } from '../api/client'
import { Severity, Status } from '../api/client'
import './ComplianceChip.css'

/**
 * Renders one obligation.
 *
 * The four states must LOOK different, not merely be different colours — that is
 * the entire point of the model. A colour-blind user, or one glancing at a phone
 * in sunlight, still has to tell "never expires" from "expired":
 *
 *   Expired        red chip, bold, with how long overdue
 *   Due soon       amber chip, bold
 *   Valid          green chip, regular weight — answered, but not shouting
 *   No expiry      slate chip reading "∞ LTT" — calm, never alarming
 *   Not applicable an em dash. No chip at all.
 *   Unknown        dashed outline, italic "? unknown"
 *
 * The rank is carried by weight rather than by withholding the fill from Valid.
 * Valid was once bare text with a dot, which read as an empty cell — most clearly
 * in the Permit column, where almost every value is valid and a column of real
 * answers looked like missing data.
 */
export function ComplianceChip({ cell }: { cell: ComplianceCell }) {
  const severity = cell.severity ?? Severity.Unknown
  const status = cell.status ?? Status.Unknown

  if (severity === Severity.NotApplicable) {
    return (
      <span className="chip chip--na" title="Not applicable to this vehicle">
        —
      </span>
    )
  }

  if (severity === Severity.NoExpiry) {
    return (
      <span className="chip chip--none" title="Lifetime or one-time tax — never expires">
        ∞ <span className="chip__detail">no expiry</span>
      </span>
    )
  }

  if (severity === Severity.Unknown || status === Status.Unknown) {
    return (
      <span className="chip chip--unknown" title="No value recorded — needs chasing">
        ? unknown
      </span>
    )
  }

  const tone =
    severity === Severity.Expired ? 'crit' : severity === Severity.DueSoon ? 'warn' : 'ok'

  // The date is wrapped rather than left as a bare text node so the grid can
  // stack the age underneath it (see td.dt .chip) without the "valid" dot
  // detaching from the date and floating onto its own line.
  return (
    <span className={`chip chip--${tone}`}>
      <span className="chip__date">{formatDate(cell.expiryDate)}</span>
      {cell.daysUntilExpiry != null && (
        <span className="chip__detail">{formatAge(cell.daysUntilExpiry)}</span>
      )}
    </span>
  )
}

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—'

  const [y, m, d] = iso.split('-')
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

  return `${d} ${months[Number(m) - 1]} ${y.slice(2)}`
}

/**
 * "1y over", "3mo", "12d". Coarse on purpose — a fleet manager needs the
 * magnitude at a glance, and "overdue by 431 days" is harder to scan than "1y over".
 */
function formatAge(days: number): string {
  const magnitude = Math.abs(days)

  const text =
    magnitude >= 730
      ? `${Math.round(magnitude / 365)}y`
      : magnitude >= 60
        ? `${Math.round(magnitude / 30)}mo`
        : `${magnitude}d`

  return days < 0 ? `${text} over` : `in ${text}`
}
